import {Injectable} from '@angular/core';
import {BasicField, ConditionParams, ConditionsList, Operator} from '@ee/common/models';
import {storedEvictionReasons} from '@ee/common/utils';


@Injectable({providedIn: 'root'})
export class ConditionEvaluatorService {

  /**
   * Operators whose comparison requires the field value and comparator to share a type. For these,
   * the actual field value is coerced the same way as the comparator (e.g. a form-entered "1"
   * becomes the number 1) so a rule like `precinct == 1` matches data of `{ precinct: "1" }`.
   * String operators (`contains`, `starts_with`, etc.) and `blank` are intentionally excluded so
   * substring/emptiness checks keep operating on the raw value.
   */
  private static readonly TYPE_ALIGNED_OPERATORS: ReadonlyArray<Operator> =
    ['==', '<=', '>=', '>', '<', 'in', 'none'];

  /**
   * `eviction_reason` is now a mirror of `eviction_reasons` (EE-392): a rule whose source field is
   * `eviction_reason` must be evaluated against every selected reason, not just the first.
   */
  private static readonly EVICTION_REASON_FIELD = 'eviction_reason';

  public evaluateConditions(conditions: ConditionsList, obj: any): boolean {
    // for now we're assuming a flat structure/no nested rules and infer AND if multiple rules listed
    // TODO: enhance rule eval logic
    let result = true;
    conditions.forEach((rule: ConditionParams) => {
      const actual = this.getFieldValue(rule.field, obj);
      // Align the field value's type with the (coerced) comparator for numeric/equality operators
      // so a string "1" entered in a form matches a rule value of 1. The undefined guard still
      // uses the raw value so an absent field evaluates to false for every operator.
      const left = ConditionEvaluatorService.TYPE_ALIGNED_OPERATORS.includes(rule.opp)
        ? this.evaluateComparator(actual, rule.opp)
        : actual;
      result = result && actual !== undefined && this.calculate(left, rule.opp, this.evaluateComparator(rule.val, rule.opp));
    });
    return result;
  }

  public evaluateRuleDefinition(sourceField: BasicField, operator: Operator, matchValue: any, obj: any): boolean {
    const actualValue = this.getFieldValue(sourceField.field, obj);
    return this.calculate(actualValue, operator, matchValue);
  }

  public evaluateRule(rule: string, obj: any): boolean {
    // const [field, opp, val] = rule.split(' ');
    // field is surrounded by hashtags, opp can be found in the OperatorList, val is anything to the right of the opp
    const field = rule.match(/#(.*?)#/)?.[1];
    const opp = rule.match(/(=|<=|>=|>|<|LIKE|IN|NOT IN)/)?.[0];
    const val = rule.match(/(?:=|<=|>=|>|<|LIKE|IN|NOT IN) (.*)/)?.[1];

    const value = this.getFieldValue(field, obj);
    return value !== undefined && this.calculate(value, opp as Operator, this.evaluateComparator(val, opp as Operator));
  }

  private getFieldValue(field: string, obj: any): any {
    let currentPropertyValue = obj;
    field.split('.').forEach((property: string) => {
      if (currentPropertyValue) { // make sure it exists
        currentPropertyValue = property === ConditionEvaluatorService.EVICTION_REASON_FIELD
          ? this.resolveEvictionReasons(currentPropertyValue)
          : currentPropertyValue[property];
      } else {
        currentPropertyValue = undefined;
      }
    });
    return currentPropertyValue;
  }

  /**
   * A rule's source field `eviction_reason` resolves to the reasons LIST, not the scalar mirror
   * (EE-392 Req 5.1), read exactly as the backend `EvictionReasonsNormalizer.normalize` reads it
   * ({@link storedEvictionReasons}): a non-empty trimmed/de-duplicated `eviction_reasons` wins, even
   * over a stale scalar; an empty or absent list falls back to `[eviction_reason]` (Req 5.2 parity).
   */
  private resolveEvictionReasons(obj: any): string[] {
    return storedEvictionReasons(obj);
  }

  private calculate(leftValue: any, operator: Operator, rightValue: any): boolean {
    if (Array.isArray(leftValue)) {
      return this.calculateForList(leftValue, operator, rightValue);
    }
    let result;
    switch (operator) {
      case '==':
        result = leftValue === rightValue;
        break;
      case '<':
        result = leftValue < rightValue;
        break;
      case '>':
        result = leftValue > rightValue;
        break;
      case '<=':
        result = leftValue <= rightValue;
        break;
      case '>=':
        result = leftValue >= rightValue;
        break;
      case 'in':
        result = Array.isArray(rightValue) && rightValue.indexOf(leftValue) > -1;
        break;
      case 'none':
        result = Array.isArray(rightValue) && rightValue.indexOf(leftValue) === -1;
        break;
      case 'contains':
        result = typeof rightValue === 'string' && typeof leftValue === 'string' &&
          rightValue.toLowerCase().includes(leftValue.toLowerCase());
        break;
      case 'not_contains':
        result = typeof rightValue === 'string' && typeof leftValue === 'string' &&
          !rightValue.toLowerCase().includes(leftValue.toLowerCase());
        break;
      case 'starts_with':
        result = typeof rightValue === 'string' && typeof leftValue === 'string' &&
          rightValue.toLowerCase().startsWith(leftValue.toLowerCase());
        break;
      case 'ends_with':
        result = typeof rightValue === 'string' && typeof leftValue === 'string' &&
          rightValue.toLowerCase().endsWith(leftValue.toLowerCase());
        break;
      case 'blank':
        result = leftValue === null || leftValue === undefined || leftValue === '';
        break;
      default:
        result = false;
    }
    return result;
  }

  /**
   * List-field variant of {@link calculate} (EE-392 Req 5.1-5.5), used when the field value resolves
   * to an array (currently a case/intake eviction's normalized `eviction_reasons`) rather than a
   * scalar. Kept in parity with the backend `RuleEvaluator.calculateForList`:
   *  - `==` - the list includes the comparison value (strict equality per element).
   *  - `in` / `none` - the list and a comparison LIST share / share no element.
   *  - `contains` / `not_contains` / `starts_with` / `ends_with` - some string element of the list
   *    satisfies the (case-insensitive) check; non-string elements are ignored. `not_contains` is the
   *    negation of that same "some element" test, not "some element `not_contains`".
   *  - `blank` - the list itself is empty (a `null`/`undefined` field value never reaches here - it
   *    isn't an array - and is already handled as blank by the scalar branch above).
   *  - relational (`<`, `>`, `<=`, `>=`) - always `false`; a list has no defined ordering.
   *
   * For a single-element list, every operator except the relational ones yields exactly the scalar
   * result for that one element (verified by a fast-check property in the spec).
   */
  private calculateForList(leftValue: any[], operator: Operator, rightValue: any): boolean {
    const strings = leftValue.filter((v): v is string => typeof v === 'string');
    switch (operator) {
      case '<':
      case '>':
      case '<=':
      case '>=':
        return false;
      case 'blank':
        return leftValue.length === 0;
      case '==':
        return leftValue.some(v => v === rightValue);
      case 'in':
        return Array.isArray(rightValue) && leftValue.some(elem => rightValue.some(r => r === elem));
      case 'none':
        return Array.isArray(rightValue) && leftValue.every(elem => !rightValue.some(r => r === elem));
      case 'contains':
        return typeof rightValue === 'string' &&
          strings.some(v => rightValue.toLowerCase().includes(v.toLowerCase()));
      case 'not_contains':
        return typeof rightValue === 'string' &&
          strings.every(v => !rightValue.toLowerCase().includes(v.toLowerCase()));
      case 'starts_with':
        return typeof rightValue === 'string' &&
          strings.some(v => rightValue.toLowerCase().startsWith(v.toLowerCase()));
      case 'ends_with':
        return typeof rightValue === 'string' &&
          strings.some(v => rightValue.toLowerCase().endsWith(v.toLowerCase()));
      default:
        return false;
    }
  }

  private evaluateComparator(comp: any, operator: Operator): any {
    let result = comp;
    if (Array.isArray(comp)) {
      result = comp.map(c => this.evaluateComparator(c, operator));
    } else if (typeof comp === 'string') {
      try {
        result = JSON.parse(comp);
      } catch {
        // do nothing
        if (comp?.toLowerCase() === 'true' || comp?.toLowerCase() === 'false') {
          result = JSON.parse(comp);
        } else if (comp?.toLowerCase() === 'null') {
          result = null;
        }
      }
    }
    return result;
  }
}
